import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { FileService } from '../file/file.service';
import { AnalyticsService, UtilityService } from 'src/app/shared/services';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { TranslateService } from '@ngx-translate/core';
import { IGalleryImage } from 'src/app/models/gallery.models';

@Injectable({ providedIn: 'root' })
export class GalleryService {
  private loader: HTMLIonLoadingElement;

  constructor(
    private fileService: FileService,
    private platform: Platform,
    private SAnalytics: AnalyticsService,
    private SUtility: UtilityService,
    private SEventData: EventDataService,
    private alertCtrl: AlertController,
    private translateService: TranslateService,
  ) {}

  openImage(image: IGalleryImage, eventId: string) {
    this.SAnalytics.imageAccess(eventId, image);
    const isNativeApp = Capacitor.isNativePlatform();

    if (isNativeApp) {
      this.openImageMobile(image);
    } else {
      this.openImageWeb(image);
    }
  }

  async downloadImage(image: IGalleryImage, eventId: string): Promise<void> {
    this.SAnalytics.imageAccess(eventId, image);
    const isNativeApp = Capacitor.isNativePlatform();
    const language = this.SEventData.getLanguage();
    const fileName = image.name?.[language] || 'image';
    const ext = image.type || 'jpg';

    if (isNativeApp) {
      await this.downloadImageMobile(image, fileName, ext);
    } else {
      await this.forceDownloadWeb(image.url, fileName, ext);
    }
  }

  async downloadSelected(images: IGalleryImage[], eventId: string): Promise<void> {
    for (const image of images) {
      await this.downloadImage(image, eventId);
    }
  }

  private async openImageMobile(image: IGalleryImage) {
    this.presentLoading();
    const language = this.SEventData.getLanguage();
    const ext = image.type || 'jpg';

    this.fileService
      .downloadFile(image.name?.[language] || 'image', ext, image.url)
      .then((response) => {
        this.fileService
          .openFile(response.path, ext)
          .catch(() => this.openFileErr(`file://${response.path}`))
          .finally(() => this.closeLoading());
      })
      .catch((error) => {
        console.error(error);
        this.genericErr();
        this.closeLoading();
      });
  }

  private openImageWeb(image: IGalleryImage): void {
    window.open(image.url, '_blank');
  }

  private async downloadImageMobile(image: IGalleryImage, fileName: string, ext: string): Promise<void> {
    this.presentLoading();
    try {
      const response = await this.fileService.downloadFile(fileName, ext, image.url);
      await this.fileService
        .openFile(response.path, ext)
        .catch(() => this.openFileErr(`file://${response.path}`));
    } catch (error) {
      console.error(error);
      this.genericErr();
    } finally {
      this.closeLoading();
    }
  }

  private async forceDownloadWeb(url: string, fileName: string, ext: string): Promise<void> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName.includes('.') ? fileName : `${fileName}.${ext}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed, falling back to window.open', error);
      window.open(url, '_blank');
    }
  }

  private async openFileErr(uri: string) {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('global.alerts.error'),
      message: this.translateService.instant('global.alerts.no_app_found_to_open_doc'),
      buttons: [
        {
          text: this.translateService.instant('global.buttons.share'),
          handler: () => Share.share({ url: uri }).catch(() => this.genericErr()),
        },
        { text: this.translateService.instant('global.buttons.back') },
      ],
    });
    alert.present();
  }

  private async genericErr() {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('global.alerts.error'),
      message: this.translateService.instant('global.alerts.generic_error'),
      buttons: [{ text: this.translateService.instant('global.buttons.back') }],
    });
    alert.present();
  }

  private async presentLoading() {
    this.loader = await this.SUtility.presentLoading('Loading...', 0, 'crescent');
    await this.loader.present();
  }

  private async closeLoading() {
    await this.loader.dismiss();
  }
}
